type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary'

interface StatusBadgeProps {
  status: string
  variant?: BadgeVariant
}

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-brand-50 text-brand-700 ring-brand-600/20',
  warning: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
  danger: 'bg-red-50 text-red-700 ring-red-600/20',
  info: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  neutral: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  primary: 'bg-brand-50 text-brand-700 ring-brand-600/20',
}

// Auto-detect variant based on common status values
function getAutoVariant(status: string): BadgeVariant {
  const s = status.toLowerCase()

  // Success states
  if (['closed_won', 'won', 'completed', 'qualified', 'converted', 'active', 'customer'].includes(s)) {
    return 'success'
  }

  // Warning states
  if (['warm', 'contacted', 'proposal', 'negotiation', 'pending', 'scheduled', 'in_progress'].includes(s)) {
    return 'warning'
  }

  // Danger states
  if (['hot', 'closed_lost', 'lost', 'unqualified', 'cancelled', 'overdue'].includes(s)) {
    return 'danger'
  }

  // Info states
  if (['cold', 'new', 'prospect', 'qualification', 'prospecting'].includes(s)) {
    return 'info'
  }

  // Primary (brand) states
  if (['partner', 'referral', 'website'].includes(s)) {
    return 'primary'
  }

  return 'neutral'
}

export function StatusBadge({ status, variant }: StatusBadgeProps) {
  const resolvedVariant = variant || getAutoVariant(status)

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${variantStyles[resolvedVariant]}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )
}

// Specific badge components for common use cases
export function LeadStatusBadge({ status }: { status: string }) {
  return <StatusBadge status={status} />
}

export function LeadRatingBadge({ rating }: { rating: string | null | undefined }) {
  if (!rating) return <span className="text-gray-400">-</span>

  const variantMap: Record<string, BadgeVariant> = {
    hot: 'danger',
    warm: 'warning',
    cold: 'info',
  }

  return <StatusBadge status={rating} variant={variantMap[rating.toLowerCase()] || 'neutral'} />
}

export function OpportunityStage({ stage }: { stage: string }) {
  const variantMap: Record<string, BadgeVariant> = {
    prospecting: 'info',
    qualification: 'info',
    proposal: 'warning',
    negotiation: 'warning',
    closed_won: 'success',
    closed_lost: 'danger',
  }

  return <StatusBadge status={stage} variant={variantMap[stage.toLowerCase()] || 'neutral'} />
}

export function EventStatusBadge({ status }: { status: string }) {
  const variantMap: Record<string, BadgeVariant> = {
    scheduled: 'info',
    completed: 'success',
    cancelled: 'danger',
  }

  return <StatusBadge status={status} variant={variantMap[status.toLowerCase()] || 'neutral'} />
}

export function AccountTypeBadge({ type }: { type: string | null | undefined }) {
  if (!type) return <span className="text-gray-400">-</span>

  const variantMap: Record<string, BadgeVariant> = {
    customer: 'success',
    prospect: 'info',
    partner: 'primary',
  }

  return <StatusBadge status={type} variant={variantMap[type.toLowerCase()] || 'neutral'} />
}
